import { Trans } from '@lingui/react';
import { Card } from '@startuptools/ui/Card';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { useWarrantProgramListViewSuspenseQuery } from '../common/warrant-programs.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { GqlPaymentAccountKind, GqlWarrantProgramKind } from '../../../graphql/base-types.graphql';
import React from 'react';
import { OptionProgramsTable } from '../common/OptionPrograms';
import { SnrBondsTable } from './SnrBondsTable';
import { CreateWarrantProgramForm } from './CreateWarrantProgram';
import { useCreateWarrantProgramMutation } from '../../../graphql/react-operations';
import { GenericErrorText } from '@startuptools/ui/Error';
import { Box } from '@startuptools/ui/joy/Box';
import { Button } from '@startuptools/ui/joy/Button';
import { ModalDialog } from '@startuptools/ui/joy/ModalDialog';
import { DialogTitle } from '@startuptools/ui/Dialog';
import { Modal } from '@startuptools/ui/joy/Modal';
import { ModalClose } from '@startuptools/ui/joy/ModalClose';
import { wpEditLink } from '../../../components/Links';
import { Route, Routes } from 'react-router-dom';
import { WpProgramRoutes } from './WarrantProgram';
import { useNavigate } from '../../../components/Navigate';

export const WarrantProgramsRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<WarrantPrograms />} />
      <Route path=":warrantProgramId" element={<WpProgramRoutes />}>
        <Route path="*" element={<WpProgramRoutes />} />
      </Route>
    </Routes>
  );
};

const WarrantPrograms = () => {
  const companyId = useCompanyId();
  const [status, setStatus] = React.useState<'active' | 'archived'>('active');
  const [showCreate, setShowCreate] = React.useState(false);
  const { navigate } = useNavigate();
  // eslint-disable-next-line sut/loading-mutation
  const [create, createState] = useCreateWarrantProgramMutation();
  const { data } = useWarrantProgramListViewSuspenseQuery({
    variables: {
      companyId,
      kinds: [GqlWarrantProgramKind.Warrant],
      fetchBonds: true,
      archived: status === 'archived',
    },
    fetchPolicy: 'cache-and-network',
  });

  const error = createState.error;

  const isDuplicateName = error?.message === 'description already in use';

  return (
    <Stack gap={3}>
      <Card sx={{ overflow: 'auto' }}>
        <Stack gap={2}>
          <Typography level="h2">
            <Trans id="Warrants" />
          </Typography>
          <Box>
            <Button
              onClick={() => {
                setShowCreate(true);
              }}
            >
              {data.warrantPrograms.totalCount ? (
                <Trans id="Create new warrant program" />
              ) : (
                <Trans id="Create your first!" />
              )}
            </Button>
          </Box>
          <OptionProgramsTable
            warrantPrograms={data.warrantPrograms.models}
            totalCount={data.warrantPrograms.totalCount}
            status={status}
            setStatus={setStatus}
            warrantKind={GqlWarrantProgramKind.Warrant}
          />
        </Stack>
      </Card>
      {data.snrBonds?.length ? <SnrBondsTable snrBonds={data.snrBonds} /> : null}
      {showCreate && (
        <Modal open onClose={() => setShowCreate(false)}>
          <ModalDialog>
            <ModalClose />

            <React.Suspense>
              <DialogTitle>
                <Trans id="Create warrant program" />
              </DialogTitle>
              <CreateWarrantProgramForm
                onClose={() => {
                  setShowCreate(false);
                }}
                loading={createState.loading}
                error={
                  isDuplicateName ? (
                    <Trans id="A warrant program with this description already exists" />
                  ) : error ? (
                    <GenericErrorText />
                  ) : undefined
                }
                type={'wp'}
                onSave={async ({ description, warrantsForQeso }) => {
                  const res = await create({
                    variables: {
                      companyId,
                      input: {
                        description,
                        warrantKind: GqlWarrantProgramKind.Warrant,
                        useRiksbankenRate: !warrantsForQeso,
                        paymentAccountKind: GqlPaymentAccountKind.Other,
                        illiquidityDiscount: '0',
                        discountPricePerWarrant: '0',
                        primaryLang: true,
                        secondaryLang: false,
                        antlerWise: false,
                        warrantsForQeso: warrantsForQeso,
                        shareClasses: [],
                      },
                    },
                  });
                  const wp = res?.data?.createWarrantProgram;
                  if (wp?.id) {
                    void navigate(wpEditLink(wp));
                  }
                }}
              />
            </React.Suspense>
          </ModalDialog>
        </Modal>
      )}
    </Stack>
  );
};
